import { cn } from '@/lib/utils';
import type { NoiseMatrix, NoiseCell } from '@/utils/noiseMatrix';

const LEVELS = [
  { value: '', label: '—' },
  { value: 'quiet', label: 'Quiet' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'lively', label: 'Lively' },
] as const;

const TIME_SLOTS = ['morning', 'afternoon', 'evening'] as const;
const TIME_LABELS: Record<string, string> = {
  morning: '6–11am',
  afternoon: '11am–4pm',
  evening: '4pm–6am',
};
const DAY_TYPES = ['weekday', 'weekend'] as const;

function emptyMatrix(): NoiseMatrix {
  const emptyCell = (): NoiseCell => ({ level: null, updated_at: null });
  return {
    weekday: { morning: emptyCell(), afternoon: emptyCell(), evening: emptyCell() },
    weekend: { morning: emptyCell(), afternoon: emptyCell(), evening: emptyCell() },
  };
}

interface NoiseMatrixEditorProps {
  value: NoiseMatrix | null;
  onChange: (matrix: NoiseMatrix) => void;
}

export default function NoiseMatrixEditor({ value, onChange }: NoiseMatrixEditorProps) {
  const matrix = value || emptyMatrix();

  const setCell = (dayType: 'weekday' | 'weekend', timeSlot: 'morning' | 'afternoon' | 'evening', level: string) => {
    const updated: NoiseMatrix = {
      ...matrix,
      [dayType]: {
        ...matrix[dayType],
        [timeSlot]: {
          level: level || null,
          updated_at: level ? new Date().toISOString() : null,
        },
      },
    };
    onChange(updated);
  };

  const selectClass =
    'w-full px-2 py-1.5 rounded-lg border border-input bg-background text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium text-foreground">Noise Levels</span>
      <p className="text-xs text-muted-foreground">Set the typical noise level for each time period.</p>

      <div className="border border-border rounded-xl overflow-hidden mt-2">
        {/* Header row */}
        <div className="grid grid-cols-[80px_1fr_1fr_1fr] bg-muted/50">
          <div className="p-2" />
          {TIME_SLOTS.map(slot => (
            <div key={slot} className="p-2 text-center">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                {slot}
              </span>
              <span className="block text-[10px] text-muted-foreground/70">{TIME_LABELS[slot]}</span>
            </div>
          ))}
        </div>

        {/* Data rows */}
        {DAY_TYPES.map(dayType => (
          <div
            key={dayType}
            className={cn(
              'grid grid-cols-[80px_1fr_1fr_1fr] border-t border-border',
            )}
          >
            <div className="p-2 flex items-center">
              <span className="text-xs font-medium text-foreground capitalize">{dayType}</span>
            </div>
            {TIME_SLOTS.map(slot => (
              <div key={slot} className="p-1.5">
                <select
                  value={matrix[dayType][slot].level || ''}
                  onChange={(e) => setCell(dayType, slot, e.target.value)}
                  className={selectClass}
                >
                  {LEVELS.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export { emptyMatrix };
